import { Fragment, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router'
import { PeopleIcon } from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { isFlagOn } from 'utils/featureFlags'
import AdminResourceLinkList from 'components/AdminResourceLinkList'
import AdminWelcomeGuide from 'components/AdminWelcomeGuide'
import Notifications from 'components/Notifications'
import AdminHsK8Setup from 'components/AdminHsK8Setup'
import MsBHUMultiSiteModal from 'components/MsBHUMultiSiteModal'
import MsBHUSingleSiteModal from 'components/MsBHUSingleSiteModal'
import PlaybookSteps from 'components/admin/PlaybookSteps'
import ImageCarousel from 'components/ImageCarousel'
import ContentfulEntryCard from 'components/ContentfulEntryCard'
import {
  Background,
  BannerInnerWrapper,
  BannerWrapper,
  Divider,
  HeaderWrapper,
  ManagementWrapper,
  PlaybookStepsWrapper,
  ResourceLinksWrapper,
  StyledAccessibleTextLink,
  StyledAdminDashboardCoreUnitsTabButton,
  StyledAdminDashboardLaunchTabButton,
  StyledAdminDashboardMonitorTabButton,
  StyledAdminDashboardPrepareTabButton,
  StyledAdminDashboardSetupTabButton,
  StyledAdminDashboardSpecializedUnitsTabButton,
  StyledAdminDashboardTier2TabButton,
  StyledAdminDashboardTier2TabMonitorLayoutButton,
  StyledAdminExportDownloadButton,
  StyledHairline,
  StyledHeaderOne,
  StyledHeaderTwo,
  StyledManageLicensesLink,
  StyledManageUsersLink,
  StyledProgramDropdown,
  StyledInterventionTabButton,
  StyledSchoolOverviewLink,
  StyledTabSet,
  TopSectionWrapper,
  UserProgressLinksWrapper,
  UserProgressTabButton,
} from './component.styles'
import {
  DASHBOARD_HEADER,
  DROPDOWN_PROGRAMS,
  FLAG_CORE_LESSON_COMPLETION_UPDATES,
  FLAG_LEADER_DASHBOARD_REDESIGN,
  GRADE_BANDS,
  MANAGE_USERS_LINK_URL,
  PRACTICE_TYPES,
  PROGRAM_DROPDOWN_LABEL,
  PROGRAM_ERROR_MESSAGE,
  PROGRAM_TITLES,
  SCHOOL_OVERVIEW_LINK_TEXT,
} from './constants'
import { TABLE_URL_PARAMS } from 'store/reportsManager/constants'

const MODAL_COMPONENTS = {
  unitSettings: ({ isOpen, mspSites, onModalCancel, onModalSave }) => {
    if (!isOpen) return null
    if (mspSites.length > 1) {
      return (
        <MsBHUMultiSiteModal
          onModalCancel={onModalCancel}
          onModalSave={onModalSave}
          rawSitesData={mspSites}
        />
      )
    }
    if (mspSites.length === 1) {
      return (
        <MsBHUSingleSiteModal
          onModalCancel={onModalCancel}
          onModalSave={onModalSave}
          site={mspSites[0]}
        />
      )
    }
    return null
  },
}

const AdminDashboardBanner = ({
  availableLinks,
  className,
  handleKeyPress,
  handleTabChange,
  hasDwbAccess,
  hasHsDwbAccess,
  hsToggleView,
  isSmallGroupLessonCompletionPage,
  isLaunched,
  isSetup,
  isSingleK8Site,
  isUserProgressPage,
  k8ToggleView,
  mspSites,
  navigationEvent,
  navigateToSameTab,
  onChangeProgram,
  onboardingSnapshot,
  programTitles,
  selectedProgram,
  sites,
  unassignedLicenses,
  updateMSPOptIn,
}) => {
  const coreLessonCompletionUpdates = isFlagOn([
    FLAG_CORE_LESSON_COMPLETION_UPDATES,
  ])
  const leaderDashboardRedesign = isFlagOn([FLAG_LEADER_DASHBOARD_REDESIGN])
  const Tier2TabComponent = coreLessonCompletionUpdates
    ? StyledAdminDashboardTier2TabButton
    : StyledAdminDashboardTier2TabMonitorLayoutButton

  const [openModalId, setOpenModalId] = useState(null)

  const handleOnModalSaveMap = {
    unitSettings: async data => {
      await updateMSPOptIn(data)
      handleCloseModal()
    },
  }
  const handleOpenModal = modalId => setOpenModalId(modalId)
  const handleCloseModal = () => setOpenModalId(null)
  const handleOnModalSave = async data => {
    if (openModalId && handleOnModalSaveMap[openModalId]) {
      await handleOnModalSaveMap[openModalId](data)
    } else {
      handleCloseModal()
    }
  }

  const createProgramOptions = () => {
    return (
      programTitles
        ?.map(programKey => ({
          name: DROPDOWN_PROGRAMS[programKey]?.name,
          key: programKey,
          sortId: DROPDOWN_PROGRAMS[programKey]?.sortId,
        }))
        .sort((a, b) => a.sortId - b.sortId) || []
    )
  }

  const createSiteOptions = currentSiteId => {
    return sites
      ?.filter(site => site.id !== currentSiteId)
      ?.map(site => ({
        name: site.name,
        key: site.id,
      }))
  }

  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ])
  const programTitle = isUserProgressPage
    ? PROGRAM_TITLES.USER_PROGRESS
    : isSmallGroupLessonCompletionPage
    ? PROGRAM_TITLES.INTERVENTIONS
    : PROGRAM_TITLES[selectedProgram?.name] || PROGRAM_ERROR_MESSAGE

  const isK8 = selectedProgram?.name === 'K8'
  /** K8 Monitor tab: `/monitor` (legacy also used `/core`). */
  const isK8LeaderDashboardMonitorPath =
    isK8 &&
    (location.pathname.includes('/admin/program/k8/monitor') ||
      location.pathname.includes('/admin/program/k8/core'))
  const isSela = selectedProgram?.name === 'SELA'
  const isBpu = selectedProgram?.name === 'BPU'
  const isCpu = selectedProgram?.name === 'CPU'
  const isDwb = selectedProgram?.name === 'DWB'
  const isHs = selectedProgram?.name === 'HS'
  const siteId = parseInt(searchParams.get(TABLE_URL_PARAMS.SITE_ID))
  const currentSite = sites?.find(site => site.id === siteId)
  const programOptions = createProgramOptions()
  const siteOptions = createSiteOptions(currentSite?.id)
  const dropdownOptions =
    isUserProgressPage || isSmallGroupLessonCompletionPage
      ? siteOptions
      : programOptions
  const defaultLabel =
    isUserProgressPage || isSmallGroupLessonCompletionPage
      ? currentSite?.name
      : PROGRAM_DROPDOWN_LABEL
  const isMSAdminUrl =
    window.location.href.includes('admin/program/k8') &&
    !isSmallGroupLessonCompletionPage
  const shouldRenderDropdown =
    (programTitles && programTitles.length > 1) ||
    ((isUserProgressPage || isSmallGroupLessonCompletionPage) &&
      sites?.length > 1)

  const schoolOverviewLinkUrl = isUserProgressPage
    ? '/admin/program/hs/monitor'
    : isSmallGroupLessonCompletionPage
    ? '/admin/program/k8/interventions'
    : null

  // Define a context object containing all possible modal props.
  // Each entry in MODAL_COMPONENTS will extract only the props it needs based on the modalId.
  const modalContext = {
    isOpen: !!openModalId,
    mspSites,
    onModalCancel: handleCloseModal,
    onModalSave: handleOnModalSave,
  }
  const ModalComponent =
    openModalId &&
    MODAL_COMPONENTS[openModalId] &&
    MODAL_COMPONENTS[openModalId](modalContext)

  return (
    <BannerWrapper>
      {selectedProgram && <Background program={selectedProgram?.name} />}
      <BannerInnerWrapper>
        {!(isUserProgressPage || isSmallGroupLessonCompletionPage) && (
          <ManagementWrapper>
            <StyledManageUsersLink
              navigateOutOfAppOnClick={navigateToSameTab}
            />
            <Divider />
            <StyledManageLicensesLink
              navigateOutOfAppOnClick={navigateToSameTab}
            />
          </ManagementWrapper>
        )}
        {(isUserProgressPage || isSmallGroupLessonCompletionPage) && (
          <StyledSchoolOverviewLink
            ariaLabel="school-overview-link"
            dataTestId="school-overview-link"
            onClick={() =>
              navigationEvent(
                schoolOverviewLinkUrl,
                NavigationHelper.types.PUSH,
              )
            }
          >
            {SCHOOL_OVERVIEW_LINK_TEXT}
          </StyledSchoolOverviewLink>
        )}
        <Notifications
          isMSAdminUrl={isMSAdminUrl}
          navigationEvent={navigationEvent}
          unassignedLicenses={unassignedLicenses}
        />
        {!isUserProgressPage && !isSmallGroupLessonCompletionPage && (
          <StyledHeaderTwo>{DASHBOARD_HEADER}</StyledHeaderTwo>
        )}
        <HeaderWrapper>
          {selectedProgram && (
            <Fragment key="admin-program-dropdown">
              <StyledHeaderOne>{programTitle}</StyledHeaderOne>
              {shouldRenderDropdown && (
                <StyledProgramDropdown
                  dataTestId="select-program"
                  defaultLabel={defaultLabel}
                  keyProp="key"
                  labelProp="name"
                  name="Select Program"
                  onChange={e => {
                    if (
                      isUserProgressPage ||
                      isSmallGroupLessonCompletionPage
                    ) {
                      navigateToSameTab(
                        `${location.pathname}?${TABLE_URL_PARAMS.SITE_ID}=${e?.key}`,
                      )
                    } else {
                      onChangeProgram(e)
                    }
                  }}
                  options={dropdownOptions}
                  selectedOption={
                    isUserProgressPage || isSmallGroupLessonCompletionPage
                      ? currentSite
                      : selectedProgram
                  }
                  value={
                    isUserProgressPage || isSmallGroupLessonCompletionPage
                      ? currentSite
                      : selectedProgram
                  }
                />
              )}
            </Fragment>
          )}
        </HeaderWrapper>
        <StyledHairline />
        {availableLinks && (
          <ResourceLinksWrapper>
            {selectedProgram &&
              !isUserProgressPage &&
              !isSmallGroupLessonCompletionPage && (
                <AdminResourceLinkList
                  availableLinks={availableLinks}
                  handleOpenModal={handleOpenModal}
                  navigationEvent={navigationEvent}
                />
              )}
            {(isUserProgressPage || isSmallGroupLessonCompletionPage) && (
              <UserProgressLinksWrapper>
                <StyledAccessibleTextLink
                  ariaLabel="manage-users-link"
                  dataTestId="manage-users-link"
                  onClick={() =>
                    navigateToSameTab(`${MANAGE_USERS_LINK_URL}/${siteId}`)
                  }
                >
                  <PeopleIcon /> Manage Users
                </StyledAccessibleTextLink>
                {!isSmallGroupLessonCompletionPage && (
                  <StyledAdminExportDownloadButton
                    buttonText="Detailed Report"
                    programKey={selectedProgram?.name}
                  />
                )}
              </UserProgressLinksWrapper>
            )}
          </ResourceLinksWrapper>
        )}
        {!isUserProgressPage && !isSmallGroupLessonCompletionPage ? (
          isSingleK8Site && isK8 ? (
            <PlaybookStepsWrapper>
              {leaderDashboardRedesign ? (
                <Fragment>
                  <PlaybookSteps showHeader={true} />
                  <PlaybookSteps
                    isSingleSite={isSingleK8Site}
                    showHeader={true}
                    showMonitorViewOnly={true}
                  />
                </Fragment>
              ) : (
                <Fragment>
                  <PlaybookSteps />
                  <ImageCarousel entryId="4Mq7SGY1JZifAWATDx8af3" />
                </Fragment>
              )}
            </PlaybookStepsWrapper>
          ) : (
            isHs && (
              <AdminHsK8Setup
                isLaunched={isLaunched}
                isSetup={isSetup}
                selectedProgram={selectedProgram}
              />
            )
          )
        ) : null}
        {isK8 && !isSingleK8Site && !isSmallGroupLessonCompletionPage && (
          <TopSectionWrapper>
            <ContentfulEntryCard entryId="2j5DzdopQMREnzQtB1AbNq" />
            <ImageCarousel entryId="4Mq7SGY1JZifAWATDx8af3" />
          </TopSectionWrapper>
        )}
        {programTitle === PROGRAM_TITLES.K8 &&
          !isSmallGroupLessonCompletionPage && (
            <StyledTabSet>
              {!isSingleK8Site &&
                (leaderDashboardRedesign ? (
                  <StyledAdminDashboardSetupTabButton
                    className={className}
                    isActive={k8ToggleView === TOGGLE_VIEW.SETUP}
                    label="Setup"
                    onClick={() => handleTabChange(TOGGLE_VIEW.SETUP)}
                    onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.SETUP)}
                  />
                ) : (
                  <StyledAdminDashboardPrepareTabButton
                    className={className}
                    isActive={k8ToggleView === TOGGLE_VIEW.PREPARE}
                    label="Prepare"
                    onClick={() => handleTabChange(TOGGLE_VIEW.PREPARE)}
                    onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.PREPARE)}
                  />
                ))}
              {!coreLessonCompletionUpdates && (
                <StyledAdminDashboardLaunchTabButton
                  className={className}
                  isActive={k8ToggleView === TOGGLE_VIEW.LAUNCH}
                  label="Launch"
                  onClick={() => handleTabChange(TOGGLE_VIEW.LAUNCH)}
                  onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.LAUNCH)}
                />
              )}
              {coreLessonCompletionUpdates ? (
                <Fragment>
                  <StyledAdminDashboardCoreUnitsTabButton
                    className={className}
                    isActive={
                      k8ToggleView === TOGGLE_VIEW.CORE_UNITS ||
                      k8ToggleView === TOGGLE_VIEW.MONITOR
                    }
                    label="Core Units"
                    onClick={() => handleTabChange(TOGGLE_VIEW.CORE_UNITS)}
                    onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.CORE_UNITS)}
                  />
                  {coreLessonCompletionUpdates && hasDwbAccess && (
                    <StyledAdminDashboardSpecializedUnitsTabButton
                      className={className}
                      isActive={k8ToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS}
                      label="Specialized Units"
                      onClick={() =>
                        handleTabChange(TOGGLE_VIEW.SPECIALIZED_UNITS)
                      }
                      onKeyPress={e =>
                        handleKeyPress(e, TOGGLE_VIEW.SPECIALIZED_UNITS)
                      }
                    />
                  )}
                </Fragment>
              ) : (
                <StyledAdminDashboardMonitorTabButton
                  className={className}
                  isActive={
                    k8ToggleView === TOGGLE_VIEW.MONITOR ||
                    isK8LeaderDashboardMonitorPath
                  }
                  label="Monitor"
                  onClick={() => handleTabChange(TOGGLE_VIEW.MONITOR)}
                  onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.MONITOR)}
                />
              )}
              {isFlagOn(['feature_LEARN-24549_Tier_2_Reporting']) && (
                <Tier2TabComponent
                  $isSpecializedUnitsTabOn={
                    coreLessonCompletionUpdates && hasDwbAccess
                  }
                  className={className}
                  isActive={k8ToggleView === TOGGLE_VIEW.INTERVENTIONS}
                  label="Interventions"
                  onClick={() => handleTabChange(TOGGLE_VIEW.INTERVENTIONS)}
                  onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.INTERVENTIONS)}
                  ultraWide={true}
                />
              )}
            </StyledTabSet>
          )}
        {programTitle === PROGRAM_TITLES.HS && (
          <StyledTabSet>
            <StyledAdminDashboardLaunchTabButton
              className={className}
              isActive={hsToggleView === TOGGLE_VIEW.LAUNCH}
              label="Launch"
              onClick={() => handleTabChange(TOGGLE_VIEW.LAUNCH)}
              onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.LAUNCH)}
            />
            <StyledAdminDashboardMonitorTabButton
              className={className}
              isActive={hsToggleView === TOGGLE_VIEW.MONITOR}
              label="Monitor"
              onClick={() => handleTabChange(TOGGLE_VIEW.MONITOR)}
              onKeyPress={e => handleKeyPress(e, TOGGLE_VIEW.MONITOR)}
            />
            {coreLessonCompletionUpdates && hasHsDwbAccess && (
              <StyledAdminDashboardSpecializedUnitsTabButton
                $isHsProgram
                className={className}
                isActive={hsToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS}
                label="Specialized Units"
                onClick={() => handleTabChange(TOGGLE_VIEW.SPECIALIZED_UNITS)}
                onKeyPress={e =>
                  handleKeyPress(e, TOGGLE_VIEW.SPECIALIZED_UNITS)
                }
              />
            )}
          </StyledTabSet>
        )}
        {isUserProgressPage && (
          <StyledTabSet isUserProgressPage={isUserProgressPage}>
            <UserProgressTabButton
              className={className}
              isActive={location.pathname.includes(
                PRACTICE_TYPES.SCHOOLWIDE_PRACTICES.url,
              )}
              label={PRACTICE_TYPES.SCHOOLWIDE_PRACTICES.text}
              onClick={() =>
                handleTabChange(PRACTICE_TYPES.SCHOOLWIDE_PRACTICES.url, siteId)
              }
              onKeyPress={e =>
                handleKeyPress(
                  e,
                  PRACTICE_TYPES.SCHOOLWIDE_PRACTICES.url,
                  siteId,
                )
              }
              wide={true}
            />
            <UserProgressTabButton
              className={className}
              isActive={location.pathname.includes(
                PRACTICE_TYPES.EDUCATOR_PRACTICES.url,
              )}
              label={PRACTICE_TYPES.EDUCATOR_PRACTICES.text}
              onClick={() =>
                handleTabChange(PRACTICE_TYPES.EDUCATOR_PRACTICES.url, siteId)
              }
              onKeyPress={e =>
                handleKeyPress(e, PRACTICE_TYPES.EDUCATOR_PRACTICES.url, siteId)
              }
              wide={true}
            />
            <UserProgressTabButton
              className={className}
              isActive={location.pathname.includes(
                PRACTICE_TYPES.STUDENT_ACTIVITIES.url,
              )}
              label={PRACTICE_TYPES.STUDENT_ACTIVITIES.text}
              onClick={() =>
                handleTabChange(PRACTICE_TYPES.STUDENT_ACTIVITIES.url, siteId)
              }
              onKeyPress={e =>
                handleKeyPress(e, PRACTICE_TYPES.STUDENT_ACTIVITIES.url, siteId)
              }
              wide={true}
            />
          </StyledTabSet>
        )}
        {isSmallGroupLessonCompletionPage && (
          <StyledTabSet
            isSmallGroupLessonCompletionPage={isSmallGroupLessonCompletionPage}
          >
            {GRADE_BANDS.map(band => {
              const isActive = location.pathname.includes(band.url)
              return (
                <StyledInterventionTabButton
                  className={className}
                  isActive={isActive}
                  key={band.url}
                  label={band.text}
                  onClick={() => handleTabChange(band.url, siteId)}
                  onKeyPress={e => handleKeyPress(e, band.url, siteId)}
                />
              )
            })}
          </StyledTabSet>
        )}
        {(isSela || isBpu || isCpu || isDwb) && (
          <Fragment>
            <AdminWelcomeGuide
              navigationEvent={navigationEvent}
              onboardingSnapshot={onboardingSnapshot}
              selectedProgram={selectedProgram}
            />
          </Fragment>
        )}
      </BannerInnerWrapper>
      {ModalComponent}
    </BannerWrapper>
  )
}

AdminDashboardBanner.propTypes = {
  availableLinks: PropTypes.array,
  className: PropTypes.string,
  handleKeyPress: PropTypes.func,
  handleTabChange: PropTypes.func,
  hasDwbAccess: PropTypes.bool,
  hasHsDwbAccess: PropTypes.bool,
  hsToggleView: PropTypes.string,
  isLaunched: PropTypes.bool,
  isSetup: PropTypes.bool,
  isSingleK8Site: PropTypes.bool,
  isSmallGroupLessonCompletionPage: PropTypes.bool,
  isUserProgressPage: PropTypes.bool,
  k8ToggleView: PropTypes.string,
  mspSites: PropTypes.array,
  navigateToSameTab: PropTypes.func,
  navigationEvent: PropTypes.func,
  onChangeProgram: PropTypes.func,
  onboardingSnapshot: PropTypes.object,
  programTitles: PropTypes.array,
  selectedProgram: PropTypes.object,
  sites: PropTypes.array,
  unassignedLicenses: PropTypes.array,
  updateMSPOptIn: PropTypes.func,
}

export default AdminDashboardBanner
